/* import __COLOCATED_TEMPLATE__ from './fin-usage-tracker-banner.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import ajax from 'embercom/lib/ajax';
import type IntlService from 'embercom/services/intl';

interface Args {}

export default class FinUsageTrackerBanner extends Component<Args> {
  @service declare appService: AppService;
  @service declare customerService: $TSFixMe;
  @service declare bannerDismissalService: $TSFixMe;
  @service declare store: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @tracked finResolutionUsage = -1;
  @tracked isBannerDismissed = false;
  @tracked isOpen = false;
  @tracked dismissalKey = '';

  get location(): string {
    return this.router.currentRouteName || '';
  }

  get finIncentiveExpiresAt(): Date | null {
    return this.customerService?.customer?.finIncentiveExpiresAt || null;
  }

  get daysRemainingInFinIncentive(): number {
    if (!this.finIncentiveExpiresAt) {
      return -1;
    }

    let cycleEnd = this.getDateOnly(
      this.customerService?.customer?.subscription?.currentCycleDate?.to,
    );

    if (this.getDateOnly(this.finIncentiveExpiresAt) >= cycleEnd) {
      let currentDate = new Date();
      return this.getDaysDifference(currentDate, cycleEnd);
    }

    return -1;
  }

  get finEndsThisCycle(): boolean {
    if (!this.finIncentiveExpiresAt) {
      return false;
    }

    let expiresAt = this.getDateOnly(this.finIncentiveExpiresAt);
    let cycleEnd = this.getDateOnly(
      this.customerService?.customer?.subscription?.currentCycleDate?.to,
    );

    return expiresAt.getTime() === cycleEnd.getTime();
  }

  get finIncentiveExpiryDate(): string {
    if (!this.finIncentiveExpiresAt) {
      return '';
    }

    return this.intl.formatDate(new Date(this.finIncentiveExpiresAt), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  private getDaysDifference(date1: Date, date2: Date): number {
    let diffTime = Math.abs(date2.getTime() - date1.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.dismissalKey = `fin-usage-tracker-banner-${this.router?.currentRouteName?.includes('apps.app.automation') ? 'automation' : 'billing'}`;
    taskFor(this.checkBannerDismissal).perform();
    if (this.finIncentiveExpiresAt) {
      this.trackFinIncentiveView();
      taskFor(this.getFinResolutionUsage).perform();
    }
  }

  get isBannerVisible() {
    return (
      this.daysRemainingInFinIncentive > 0 &&
      !this.isBannerDismissed &&
      this.finResolutionUsage >= 0 &&
      this.router.currentRouteName !== 'apps.app.automation.fin-ai-agent.content-suggestions'
    );
  }

  private trackFinIncentiveView() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'viewed',
      object: 'fin_usage_tracker_banner',
      place: this.location,
    });
  }

  private getDateOnly(date: Date): Date {
    let dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0); // Set to midnight
    return dateOnly;
  }

  @restartableTask
  *getFinResolutionUsage(): Generator<any, void, any> {
    try {
      let usage = yield ajax({
        url: '/ember/pricing_model_daily_app_stats/fetch',
        type: 'GET',
        data: { app_id: this.appService.app.id, current_period: true },
      });
      this.finResolutionUsage =
        usage.firstObject.resolutions_with_custom_answers > 250
          ? 250
          : usage.firstObject.resolutions_with_custom_answers;
    } catch (error) {
      this.finResolutionUsage = -1;
    }
  }

  @restartableTask
  *checkBannerDismissal(): Generator<any, void, any> {
    let hasDismissed: boolean = yield this.bannerDismissalService.hasDismissed(this.dismissalKey);
    this.isBannerDismissed = hasDismissed;
  }

  @action
  async dismissBanner() {
    await this.bannerDismissalService.dismiss(this.dismissalKey, { expiresIn: '1d' });
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'fin_usage_tracker_banner_dismiss_button',
      place: this.location,
    });
    this.isBannerDismissed = true;
  }

  @action
  onModalOpen() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'fin_usage_tracker_banner_cta',
      place: this.location,
    });
    this.isOpen = true;
  }

  @action
  onModalClose(object: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object,
      context: 'fin_resolution_understanding_modal',
      place: this.location,
    });
    this.isOpen = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'IncentiveOffer::FinUsageTrackerBanner': typeof FinUsageTrackerBanner;
    'incentive-offer/fin-usage-tracker-banner': typeof FinUsageTrackerBanner;
  }
}
